import React from "react"
import ReactDOM from "react-dom/client"
import "./main.css"
import { Provider } from "react-redux"
import { store } from "./reduxStore/store"
import { App } from "./App"
import { AppInsightsContext } from "@microsoft/applicationinsights-react-js"
import { reactPlugin } from "./features/logging/ApplicationInsightsService"
import { posthog } from "./posthogInstance"
import { PostHogProvider } from "posthog-js/react"
import ReactGA from "react-ga4"
import { isEmpty } from "lodash"

// Expose a test-only API for Playwright and CI.
// Controlled by infra flag `window._env_.EXPOSE_TEST_STORE` (should be set by your platform)
// and disabled in production builds. Also allowed on localhost for local debugging.
// NOTE: this only affects the local browser session where it's enabled.
const isLocalhost =
  typeof window !== "undefined" &&
  (window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1" ||
    window.location.hostname === "[::1]")
// TODO: need to double check these - this may be overkill but it ensures no accidental exposure
const isRestrictedEnv =
  !!process.env.NODE_ENV &&
  ["production", "uat", "stage"].includes(process.env.NODE_ENV)

// Only enable in non-production bundles and when infra enables it (or on localhost).
const exposeTestStore =
  !isRestrictedEnv && (Boolean(window._env_?.EXPOSE_TEST_STORE) || isLocalhost)

if (exposeTestStore) {
  // Ensure the test namespace object exists before assigning properties
  window.__TEST__ = window.__TEST__ ?? {}

  // Expose a minimal surface: store reference (for existing tests) and helper methods.
  window.__TEST__.store = store
  window.__TEST__.getState = () => store.getState()
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  window.__TEST__.dispatch = (action: any) => store.dispatch(action)
}

const initializeMSW =
  !isRestrictedEnv &&
  typeof window._env_?.ENABLE_MSW === "boolean" &&
  window._env_?.ENABLE_MSW &&
  isLocalhost
if (initializeMSW) {
  const { worker } = await import("./mocks/browser")
  console.warn("Starting MSW worker...")
  worker.start()
}

if (!isEmpty(window._env_?.GOOGLE_ANALYTICS_MEASUREMENT_ID)) {
  ReactGA.initialize([
    {
      gaOptions: { forceSSL: true },
      trackingId: window._env_?.GOOGLE_ANALYTICS_MEASUREMENT_ID,
    },
  ])
}

ReactDOM.createRoot(document.getElementById("root")!).render(
  <Provider store={store}>
    <AppInsightsContext.Provider value={reactPlugin}>
      <PostHogProvider client={posthog}>
        <React.StrictMode>
          <App />
        </React.StrictMode>
      </PostHogProvider>
    </AppInsightsContext.Provider>
  </Provider>,
)
